import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ImageOff, Pencil, Plus, Trash } from "lucide-react";
import {
  AdminShopCatalog,
  ShopProduct,
  deleteShopProduct,
  getAdminShopCatalog,
  getAdminShopProducts,
  reorderShopProducts,
  updateShopProduct,
} from "../../../api/shop";
import { useAppDispatch } from "../../../store/store";
import { setGeneralAlertMessage } from "../../../store/slices/generalSlice";
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Loader,
  PageHeader,
  SearchInput,
  Switch,
  TableWrap,
  Tag,
  Toolbar,
} from "../../../ui";
import { sportTone } from "../shopFormat";
import { activeToggleProblem, toSaveShopProductRequest } from "../shopRequest";
import { ReorderControls } from "../shared/ReorderControls";
import { useLatched } from "../shared/useLatched";
import { usePersistedOrder } from "../shared/useReorder";
import { CatalogStatus, ProductEditor } from "./ProductEditor";
import { categoryPath, categoryPathParts, matchesProductSearch, priceLabel, productCover, productSports, stockSummary } from "./productSummary";
import "../styles.scss";
import "../shared/styles.scss";
import "./styles.scss";

const EMPTY_CATALOG: AdminShopCatalog = { sizes: [], optionTypes: [], categories: [], sports: [] };
const SEARCH_REORDER_REASON = "Clear the search to reorder";

const Cover = ({ product }: { product: ShopProduct }) => {
  const url = productCover(product);
  return <div className="product-thumb">{url ? <img src={url} alt={product.name} /> : <ImageOff size={16} />}</div>;
};

// Each part stays on one line; a narrow column breaks the line only at " · "
const StockSummary = ({ product }: { product: ShopProduct }) => (
  <span className="stock-summary">
    {stockSummary(product).map((part, i) => (
      <Fragment key={i}>
        {i > 0 && <span className="stock-sep"> · </span>}
        <span className={["stock-part", part.tone === "danger" ? "out-of-stock" : part.tone === "muted" ? "muted-text" : ""].filter(Boolean).join(" ")}>
          {part.text}
        </span>
      </Fragment>
    ))}
  </span>
);

// "Apparel › T-shirts" breaks the line only at " › ", never inside a name
const CategoryPath = ({ product, catalog }: { product: ShopProduct; catalog: AdminShopCatalog }) => {
  const parts = categoryPathParts(product, catalog.categories);
  if (!parts) return <span className="muted-text">-</span>;
  return (
    <span className="category-path">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && " › "}
          <span className="nowrap">{part}</span>
        </Fragment>
      ))}
    </span>
  );
};

const SportTags = ({ product, catalog }: { product: ShopProduct; catalog: AdminShopCatalog }) => {
  const sports = productSports(product, catalog.sports);
  return (
    <span className="sport-tags">
      {sports.length === 0 ? (
        <Tag>All sports</Tag>
      ) : (
        sports.map((s) => (
          <Tag key={s.id} tone={sportTone(s.name)}>
            {s.name}
          </Tag>
        ))
      )}
    </span>
  );
};

const ShopProducts = () => {
  const dispatch = useAppDispatch();
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [catalog, setCatalog] = useState<AdminShopCatalog>(EMPTY_CATALOG);
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>({ state: "loading" });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [editing, setEditing] = useState<ShopProduct | null>(null);
  const [productToDelete, setProductToDelete] = useState<ShopProduct | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showError = useCallback(
    (err: any, fallback: string) => dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || fallback })),
    [dispatch]
  );

  // Resolves with the list read (the editor takes its product's fresh stock from it), or null when it failed
  const fetchProducts = useCallback(async (): Promise<ShopProduct[] | null> => {
    setLoading(true);
    try {
      const res = await getAdminShopProducts();
      const list = Array.isArray(res?.data) ? res.data : [];
      setProducts(list);
      return list;
    } catch (err: any) {
      console.error("Error fetching shop products", err);
      setProducts([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetched again (quietly) each time the editor opens, so sizes added in another tab show up. Once loaded, the
  // catalog stays usable while it is read again; until then the editor waits for it.
  const fetchCatalog = useCallback(
    async (quiet = false) => {
      setCatalogStatus((prev) => (prev.state === "ready" ? prev : { state: "loading" }));
      try {
        const res = await getAdminShopCatalog();
        if (!res?.data) throw new Error("Failed to load the catalog");
        setCatalog(res.data);
        setCatalogStatus({ state: "ready" });
      } catch (err: any) {
        console.error("Error fetching the shop catalog", err);
        setCatalogStatus((prev) => (prev.state === "ready" ? prev : { state: "failed", message: err?.message || "Failed to load the catalog" }));
        if (!quiet) showError(err, "Failed to load the catalog");
      }
    },
    [showError]
  );

  useEffect(() => {
    document.title = "Shop Products";
    fetchProducts();
    fetchCatalog();
  }, [fetchProducts, fetchCatalog]);

  const order = usePersistedOrder(products, (p) => p.id, reorderShopProducts, (err) => showError(err, "Failed to reorder products"), setProducts);
  const query = search.trim();
  const searching = query !== "";
  const shown = useMemo(() => order.list.filter((p) => matchesProductSearch(p, query)), [order.list, query]);

  const openEditor = (product: ShopProduct | null) => {
    setEditing(product);
    setEditorKey((k) => k + 1);
    setEditorOpen(true);
    fetchCatalog(true);
  };

  const handleSaved = async () => {
    setEditorOpen(false);
    await fetchProducts();
  };

  const handleToggleActive = async (product: ShopProduct) => {
    if (togglingId) return;
    const problem = activeToggleProblem(product);
    if (problem) {
      dispatch(setGeneralAlertMessage({ type: "error", message: problem }));
      return;
    }
    setTogglingId(product.id);
    try {
      // Toggle from a fresh copy: the save carries expectedStock, so a stale list row would be refused
      const fresh = (await getAdminShopProducts())?.data?.find((p) => p.id === product.id) ?? product;
      const res = await updateShopProduct(product.id, toSaveShopProductRequest(fresh, { isActive: !product.isActive }));
      const saved = res?.data;
      setProducts((prev) => prev.map((p) => (p.id === product.id ? (saved?.id ? saved : { ...p, isActive: !product.isActive }) : p)));
    } catch (err: any) {
      showError(err, "Failed to update product");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;
    setDeleting(true);
    try {
      await deleteShopProduct(productToDelete.id);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Product deleted successfully" }));
      setProductToDelete(null);
      await fetchProducts();
    } catch (err: any) {
      showError(err, "Failed to delete product");
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = products.filter((p) => p.isActive).length;
  const shownToDelete = useLatched(!!productToDelete, productToDelete);

  const activeSwitch = (product: ShopProduct) => {
    const label = product.isActive ? "Hide from the shop" : "Show in the shop";
    return (
      <Switch
        checked={product.isActive}
        disabled={togglingId === product.id}
        onChange={() => handleToggleActive(product)}
        label={label}
        hideLabel
      />
    );
  };

  const reorderControls = (index: number, withHandle: boolean) => (
    <ReorderControls
      index={index}
      count={shown.length}
      onStep={order.step}
      disabled={searching || order.saving}
      disabledReason={searching ? SEARCH_REORDER_REASON : undefined}
      handleProps={withHandle && !searching ? order.handleProps(index) : undefined}
    />
  );

  return (
    <div className="shop-page shop-products-page">
      <PageHeader
        title="Products"
        subtitle={`${products.length} ${products.length === 1 ? "Product" : "Products"} · ${activeCount} active · shown in this order in the shop`}
        actions={
          <Button icon={<Plus size={18} />} onClick={() => openEditor(null)}>
            Add Product
          </Button>
        }
      />

      {products.length > 0 && (
        <Toolbar>
          <SearchInput className="products-search" value={search} onChange={setSearch} placeholder="Search name, code or SKU" />
        </Toolbar>
      )}

      {loading && products.length === 0 ? (
        <Loader />
      ) : products.length === 0 ? (
        <Card padding="none">
          <EmptyState title="No products yet." description="Products you add will appear in the shop on the website and app." />
        </Card>
      ) : shown.length === 0 ? (
        <Card padding="none">
          <EmptyState title="No products match this search." />
        </Card>
      ) : (
        <>
          <TableWrap className="shop-table-wrap">
            <table className="ui-table shop-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Sports</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Active</th>
                  <th>Order</th>
                  <th className="ui-table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((product, index) => (
                  <tr
                    key={product.id}
                    className={[product.isActive ? "" : "inactive-row", order.dragIndex === index ? "reorder-dragging" : ""].filter(Boolean).join(" ")}
                    {...(searching ? {} : order.rowProps(index))}
                  >
                    <td>
                      <Cover product={product} />
                    </td>
                    <td className="product-name">
                      <div className="ui-table__primary">{product.name}</div>
                      {product.code && <div className="ui-table__secondary product-code">{product.code}</div>}
                    </td>
                    <td>
                      <CategoryPath product={product} catalog={catalog} />
                    </td>
                    <td>
                      <SportTags product={product} catalog={catalog} />
                    </td>
                    <td className="nowrap">{priceLabel(product)}</td>
                    <td>
                      <StockSummary product={product} />
                    </td>
                    <td>
                      <span className="active-switch" data-tooltip={product.isActive ? "Hide from the shop" : "Show in the shop"}>
                        {activeSwitch(product)}
                      </span>
                    </td>
                    <td>{reorderControls(index, true)}</td>
                    <td className="ui-table__actions">
                      <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => openEditor(product)} />
                      <IconButton label="Delete" variant="danger" icon={<Trash size={16} />} onClick={() => setProductToDelete(product)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          {/* Phones: the same rows as cards (the table above is hidden under 768px) */}
          <div className="shop-cards">
            {shown.map((product, index) => {
              const path = categoryPath(product, catalog.categories);
              return (
                <Card key={product.id} padding="sm" className={`shop-card product-card ${product.isActive ? "" : "is-inactive"}`}>
                  <div className="product-card__main">
                    <Cover product={product} />
                    <div className="product-card__info">
                      <p className="product-card__name">{product.name}</p>
                      {product.code && <p className="product-card__code">{product.code}</p>}
                      {path && <p className="product-card__category">{path}</p>}
                      <SportTags product={product} catalog={catalog} />
                      <p className="product-card__price">{priceLabel(product)}</p>
                      <div className="product-card__stock">
                        <StockSummary product={product} />
                      </div>
                    </div>
                  </div>
                  <div className="shop-card__actions">
                    <IconButton size="sm" label="Edit" icon={<Pencil size={16} />} onClick={() => openEditor(product)} />
                    <IconButton size="sm" label="Delete" variant="danger" icon={<Trash size={16} />} onClick={() => setProductToDelete(product)} />
                  </div>
                  <div className="product-card__foot">
                    <span className="product-card__active">
                      {activeSwitch(product)}
                      <span aria-hidden="true">Active</span>
                    </span>
                    {reorderControls(index, false)}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {editorKey > 0 && (
        <ProductEditor
          key={editorKey}
          open={editorOpen}
          product={editing}
          catalog={catalog}
          catalogStatus={catalogStatus}
          onRetryCatalog={() => fetchCatalog()}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
          onSaveFailed={fetchProducts}
        />
      )}

      <ConfirmDialog
        open={!!productToDelete}
        onClose={(confirmed) => {
          // A successful delete clears it itself; a failed one stays open, as before
          if (!confirmed) setProductToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        title="Delete Product"
        message={
          <>
            Are you sure you want to delete "<strong>{shownToDelete?.name}</strong>"? It will be removed from the shop. Past
            orders keep their details.
          </>
        }
        confirmLabel="Delete"
      />
    </div>
  );
};

export default ShopProducts;
